/**
 * Whether Fias Forge should be offered to the signed-in user, and whether
 * their plan lets them use it (ADR-023 D8).
 *
 * Reads the same `GET /v1/forge/usage` query the usage panel does (one cache
 * entry per user), which reports the platform kill switch (`enabled`) and
 * plan eligibility (`eligible`). Any failure reads as "not offered": a
 * surface that cannot confirm Forge is on must not advertise it.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';

export interface ForgeAvailability {
  /** The kill switch is on — show Forge as an option at all. */
  offered: boolean;
  /** The user's plan can run forges (Standard/Pro). */
  eligible: boolean;
  isLoading: boolean;
}

export function useForgeAvailability(): ForgeAvailability {
  const query = useQuery({
    queryKey: forgeQueryKeys.usage(),
    queryFn: () => api.getForgeUsage(),
    staleTime: 60_000,
    retry: false,
  });
  return {
    offered: query.data?.enabled === true,
    eligible: query.data?.eligible === true,
    isLoading: query.isLoading,
  };
}
